import { toast as toastify, type ToastOptions, type Id as ToastId, type TypeOptions } from 'react-toastify';
import {
  IconAlertTriangle,
  IconCheck,
  IconInfoCircle,
  IconStar,
  IconX,
} from '@tabler/icons-react';
import { cn } from '../tokens/cn';
import { Text } from '../typography/text';

export type ToastTone = 'success' | 'error' | 'warning' | 'info' | 'brand';
export type { ToastId };

const toneToType: Record<ToastTone, TypeOptions> = {
  success: 'success',
  error: 'error',
  warning: 'warning',
  info: 'info',
  brand: 'default',
};

const toneStyles: Record<
  ToastTone,
  { surface: string; iconWrap: string; icon: typeof IconCheck; progress: string }
> = {
  success: {
    surface: 'border-l-4 border-l-[var(--tk-success-bg)]',
    iconWrap: 'bg-[var(--tk-success-bg-subtle)] text-[var(--tk-success-fg-on-subtle)]',
    icon: IconCheck,
    progress: '!bg-[var(--tk-success-bg)]',
  },
  error: {
    surface: 'border-l-4 border-l-[var(--tk-danger-bg)]',
    iconWrap: 'bg-[var(--tk-danger-bg-subtle)] text-[var(--tk-danger-fg-on-subtle)]',
    icon: IconX,
    progress: '!bg-[var(--tk-danger-bg)]',
  },
  warning: {
    surface: 'border-l-4 border-l-[var(--tk-warning-bg)]',
    iconWrap: 'bg-[var(--tk-warning-bg-subtle)] text-[var(--tk-warning-fg-on-subtle)]',
    icon: IconAlertTriangle,
    progress: '!bg-[var(--tk-warning-bg)]',
  },
  info: {
    surface: 'border-l-4 border-l-[var(--tk-info-bg)]',
    iconWrap: 'bg-[var(--tk-info-bg-subtle)] text-[var(--tk-info-fg-on-subtle)]',
    icon: IconInfoCircle,
    progress: '!bg-[var(--tk-info-bg)]',
  },
  brand: {
    surface: 'border-l-4 border-l-[var(--tk-brand-bg)]',
    iconWrap: 'bg-[var(--tk-brand-bg-subtle)] text-[var(--tk-brand-fg-on-subtle)]',
    icon: IconStar,
    progress: '!bg-[var(--tk-brand-bg)]',
  },
};

export interface ToastShowOptions extends Omit<ToastOptions, 'type'> {
  title?: string;
}

const ToastBody = ({
  title,
  message,
  tone,
}: {
  title?: string;
  message: React.ReactNode;
  tone: ToastTone;
}) => {
  const { iconWrap, icon: Icon } = toneStyles[tone];
  return (
    <div className="flex items-start gap-3">
      <span className={cn('shrink-0 rounded-tk-full p-1.5', iconWrap)}>
        <Icon size={16} aria-hidden />
      </span>
      <div className="flex-1 min-w-0">
        {title && (
          <Text as="p" size="sm" weight="semibold" className="mb-0.5">
            {title}
          </Text>
        )}
        <Text as="p" size="sm" color="muted">
          {message}
        </Text>
      </div>
    </div>
  );
};

const showToast = (
  tone: ToastTone,
  message: React.ReactNode,
  options: ToastShowOptions = {},
): ToastId => {
  const { title, ...rest } = options;
  const { surface, progress } = toneStyles[tone];
  return toastify(<ToastBody title={title} message={message} tone={tone} />, {
    type: toneToType[tone],
    icon: false,
    ...rest,
    className: cn(
      '!bg-[var(--tk-surface-bg-elevated)] !text-[var(--tk-surface-fg)] !rounded-tk-lg !shadow-tk-overlay',
      surface,
      rest.className,
    ),
    progressClassName: cn(progress, rest.progressClassName),
  });
};

export const toast = {
  success: (message: React.ReactNode, options?: ToastShowOptions) =>
    showToast('success', message, options),
  error: (message: React.ReactNode, options?: ToastShowOptions) =>
    showToast('error', message, options),
  warning: (message: React.ReactNode, options?: ToastShowOptions) =>
    showToast('warning', message, options),
  info: (message: React.ReactNode, options?: ToastShowOptions) =>
    showToast('info', message, options),
  brand: (message: React.ReactNode, options?: ToastShowOptions) =>
    showToast('brand', message, options),
  dismiss: (id?: ToastId) => toastify.dismiss(id),
};
